import { api } from './api-base';
import { BasemapId, Topic, TopicVisibility } from '../types';

export interface TopicsResponse {
  data: Topic[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Topics API
export const topicsApi = {
  // Get all topics with filtering
  getTopics: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: 'popular' | 'recent' | 'oldest' | 'active';
    country?: string;
    region?: string;
    city?: string;
    language?: string;
    search?: string;
    bbox?: string; // minLng,minLat,maxLng,maxLat
  }): Promise<TopicsResponse> => {
    const response = await api.get('/topics', { params });
    return response.data;
  },

  // Get single topic with comments
  getTopic: async (id: string): Promise<Topic> => {
    const response = await api.get(`/topics/${id}`);
    return response.data;
  },

  // Create new topic
  createTopic: async (topicData: {
    title: string;
    content: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    region?: string;
    city?: string;
    locationLabel?: string;
    locationFullLabel?: string;
    language?: string;
    basemap?: BasemapId;
    visibility?: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
    isAnonymous?: boolean;
  }): Promise<Topic> => {
    const response = await api.post('/topics', topicData);
    return response.data;
  },

  // Update topic
  updateTopic: async (id: string, topicData: {
    title?: string;
    content?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    region?: string;
    city?: string;
    locationLabel?: string;
    locationFullLabel?: string;
    basemap?: BasemapId;
    visibility?: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
  }): Promise<Topic> => {
    const response = await api.put(`/topics/${id}`, topicData);
    return response.data;
  },

  // Delete topic
  deleteTopic: async (id: string): Promise<void> => {
    await api.delete(`/topics/${id}`);
  },

  // Moderator delete topic (hard delete with moderation record)
  moderatorDeleteTopic: async (id: string, reason?: string): Promise<void> => {
    await api.delete(`/admin/topics/${id}`, { 
      params: { 
        reason: reason || undefined
      }
    });
  },

  // Vote on topic
  voteTopic: async (id: string, voteType: 'UPVOTE' | 'DOWNVOTE'): Promise<Topic> => {
    const response = await api.post(`/topics/${id}/vote`, { voteType });
    return response.data;
  },
};
